export interface WidgetRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfPageConfig {
  pageWidth: number;
  pageHeight: number;
  margin: number;
  headerHeight: number;
}

export interface PdfPlacement {
  index: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Band {
  indices: number[];
  top: number;
  onScreenBottom: number;
  effectiveHeight: number;
}

const OVERLAP_EPSILON_PX = 1;

function groupIntoBands(rects: readonly WidgetRect[], effectiveHeights: readonly number[]): Band[] {
  const order = rects
    .map((_, index) => index)
    .sort((first, second) => rects[first].y - rects[second].y || rects[first].x - rects[second].x);

  const bands: Band[] = [];
  for (const index of order) {
    const rect = rects[index];
    const current = bands[bands.length - 1];
    if (current && rect.y < current.onScreenBottom - OVERLAP_EPSILON_PX) {
      current.indices.push(index);
      current.onScreenBottom = Math.max(current.onScreenBottom, rect.y + rect.height);
      current.effectiveHeight = Math.max(
        current.effectiveHeight,
        rect.y - current.top + effectiveHeights[index]
      );
    } else {
      bands.push({
        indices: [index],
        top: rect.y,
        onScreenBottom: rect.y + rect.height,
        effectiveHeight: effectiveHeights[index],
      });
    }
  }
  return bands;
}

export function buildPdfLayout(
  rects: readonly WidgetRect[],
  effectiveHeights: readonly number[],
  containerWidth: number,
  config: PdfPageConfig
): PdfPlacement[][] {
  if (rects.length === 0 || containerWidth <= 0) return [];

  const contentWidth = config.pageWidth - config.margin * 2;
  const contentTop = config.margin + config.headerHeight;
  const contentHeight = config.pageHeight - config.margin - contentTop;
  const pageBottom = contentTop + contentHeight;
  const scale = contentWidth / containerWidth;

  const bands = groupIntoBands(rects, effectiveHeights);

  const pages: PdfPlacement[][] = [];
  let currentPage: PdfPlacement[] = [];
  let cursorY = contentTop;
  let previousOnScreenBottom: number | null = null;

  for (const band of bands) {
    const bandHeight = band.effectiveHeight * scale;
    const fit = Math.min(1, contentHeight / bandHeight);
    const gap =
      previousOnScreenBottom === null
        ? 0
        : Math.max(0, band.top - previousOnScreenBottom) * scale;

    let startY = currentPage.length === 0 ? contentTop : cursorY + gap;
    if (currentPage.length > 0 && startY + bandHeight * fit > pageBottom) {
      pages.push(currentPage);
      currentPage = [];
      startY = contentTop;
    }

    for (const index of band.indices) {
      const rect = rects[index];
      currentPage.push({
        index,
        x: config.margin + rect.x * scale * fit,
        y: startY + (rect.y - band.top) * scale * fit,
        width: rect.width * scale * fit,
        height: effectiveHeights[index] * scale * fit,
      });
    }

    cursorY = startY + bandHeight * fit;
    previousOnScreenBottom = band.onScreenBottom;
  }

  if (currentPage.length > 0) pages.push(currentPage);
  return pages;
}

export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function buildExportFileName(branchName: string, period: string): string {
  const branchSlug = slugify(branchName);
  const periodSlug = period.toLowerCase();
  return branchSlug
    ? `dashboard-${branchSlug}-${periodSlug}.pdf`
    : `dashboard-${periodSlug}.pdf`;
}

export function buildGeneratedAtLabel(date: Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'America/Sao_Paulo',
  }).format(date);
}
